import { ReservationCta, type ReservationCtas } from './reservationCta';
import {
  mapLocalReservationSignatureStatus,
  mapSalesforceReservationStatus,
} from './mapSalesforceReservationStatus';
import {
  RESERVATION_STATUS_CONFIG,
  type LocalReservationSignatureStatus,
  type ReservationStatus,
} from './reservationStatus';

export interface GetReservationCtasInput {
  /**
   * Raw Salesforce reservation status from the unit list/detail payload.
   * Empty / omitted = not reserved unless `isLocallyReserved` is set.
   */
  readonly salesforceStatus?: string | null;
  /** True when GET upcoming returned an appointment for this unit. */
  readonly appointmentExists: boolean;
  /**
   * Local pending reservation (session/store) until SF status is on the unit.
   * Ignored when `salesforceStatus` maps to a reserved status.
   */
  readonly isLocallyReserved?: boolean;
  readonly localSignatureStatus?: LocalReservationSignatureStatus | null;
}

function buildCtas(
  status: ReservationStatus | null,
  appointmentExists: boolean,
): ReservationCtas {
  const appointment = appointmentExists
    ? ReservationCta.VIEW_APPOINTMENT
    : ReservationCta.BOOK_APPOINTMENT;

  if (!status) {
    return {
      reservation: ReservationCta.RESERVE_UNIT,
      appointment,
      canCancelReservation: false,
      canResendEmail: false,
    };
  }

  const flags = RESERVATION_STATUS_CONFIG[status];
  return {
    reservation: ReservationCta.VIEW_RESERVATION,
    appointment,
    canCancelReservation: flags.canCancelReservation,
    canResendEmail: flags.canResendEmail,
  };
}

function resolveReservationStatus(
  input: GetReservationCtasInput,
): ReservationStatus | null {
  const fromSalesforce = mapSalesforceReservationStatus(input.salesforceStatus);
  if (fromSalesforce) {
    return fromSalesforce;
  }
  return mapLocalReservationSignatureStatus(
    input.isLocallyReserved === true,
    input.localSignatureStatus,
  );
}

/**
 * Unit-details CTAs from reservation status + appointment.
 *
 * - Footer left: `reservation` (Reserve Unit | View Reservation)
 * - Footer right: `appointment` (Book | View Appointment)
 * - Cancel / Resend: reservation view only
 */
export function getReservationCtas(
  input: GetReservationCtasInput,
): ReservationCtas {
  return buildCtas(
    resolveReservationStatus(input),
    input.appointmentExists,
  );
}
